import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { localStore } from '../../../v2/js/utils/storage';
import {
  AuthService,
  RegistrationStatusEnum,
  UserCompleteRegistration,
  UserTypeEnum,
} from '../../api';
import { FormCheckboxField } from '../../app/components/FormCheckboxField';
import { FormFeedback } from '../../app/components/FormFeedback';
import { FormInputMaskField } from '../../app/components/FormInputMaskField';
import { FormTextField } from '../../app/components/FormTextField';
import { Logo } from '../../app/components/Logo';
import { FormSubmitContext } from '../../app/contexts/FormSubmitContext';
import { SessionContext } from '../../app/contexts/SessionContext';
import { useDocumentTitle } from '../../app/hooks/use-document-title';
import { userQueryKeys } from '../../app/query-keys-factories/user';
import { setFormErrorsFromApiErrors } from '../../utils/forms';

const healthCareEntityRepFieldsValidation = {
  healthcare_entity_rep__healthcare_entity__name: z
    .string()
    .min(1, { message: 'Healthcare Entity Name is required' }),
  healthcare_entity_rep__title: z.string().min(1, { message: 'Job Title is required' }),
};

const coreValidationSchema = {
  first_name: z.string().min(1, { message: 'First Name is required' }),
  last_name: z.string().min(1, { message: 'Last Name is required' }),
  phone_number: z.string().min(1, { message: 'Phone Number is required' }),
  text_opt_in: z.boolean(),
};

const validationSchemaForProvider = z.object({
  ...coreValidationSchema,
});

const validationSchemaForRep = z.object({
  ...coreValidationSchema,
  ...healthCareEntityRepFieldsValidation,
});

export const SocialSignup = () => {
  useDocumentTitle('Complete Registration');
  // Setup dispatch and navigate for later use
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(SessionContext);
  const { isSubmitEnabled } = useContext(FormSubmitContext);

  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    clearErrors,
    reset,
  } = useForm({
    resolver: zodResolver(
      user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP
        ? validationSchemaForRep
        : validationSchemaForProvider
    ),
  });

  // Capture UTM parameters on component mount
  useEffect(() => {
    const utmParams = Array.from(searchParams.entries()).reduce(
      (acc, [key, value]) => {
        if (key.startsWith('utm_')) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    // Store UTM parameters if any exist
    if (Object.keys(utmParams).length > 0) {
      localStore.set('attributionSignUpCampaign', JSON.stringify(utmParams));
    }
  }, [searchParams]);

  useEffect(() => {
    reset({
      // TODO: Joe
      // should we use defaultValues in useForm instead?
      email: user?.email,
      first_name: user?.first_name,
      last_name: user?.last_name,
      phone_number: user?.phone_number,
      text_opt_in: false,
    });
  }, [user, reset]);

  useEffect(() => {
    if (user?.registration_status === RegistrationStatusEnum.PROVISIONAL) {
      navigate('/app');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.first_name?.trim() && user?.last_name?.trim() && user?.phone_number?.trim()) {
      navigate('/app');
    }
  }, [user, navigate]);

  const handleSubmitClick = () => {
    clearErrors();
  };

  const queryClient = useQueryClient();
  const mutationCompleteRegistration = useMutation({
    mutationFn: async (data: UserCompleteRegistration) => {
      await AuthService.authRegistrationCompleteUpdate({ requestBody: data });
    },
    onSuccess: () => {
      // Push event to GTM
      if (window.TAG_GOOGLE_TAG_MANAGER_ID) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'registration_complete',
          user_type: user?.user_type,
          registration_method: 'social',
        });
      }

      queryClient.invalidateQueries({ queryKey: userQueryKeys.all() });
    },
  });

  // Callback for registration form submission
  const onSubmit = async ({
    first_name,
    last_name,
    phone_number,
    healthcare_entity_rep__healthcare_entity__name,
    healthcare_entity_rep__title,
    text_opt_in,
  }) => {
    const swagScanId = localStore.get('attributionSwagScanId') || localStore.get('swag_scan_id');
    const signUpCampaign = localStore.get('attributionSignUpCampaign');
    const metadata: Record<string, string> = {};
    if (swagScanId) {
      metadata.swag_scan_id = swagScanId;
    }
    if (signUpCampaign) {
      metadata.utm_params = JSON.parse(signUpCampaign);
    }
    const userCompleteRegistrationData: UserCompleteRegistration = {
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      ...(Object.keys(metadata).length > 0 && { metadata }),
      text_opt_in,
    };
    if (user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
      userCompleteRegistrationData.healthcare_entity_rep = {
        healthcare_entity: {
          name: healthcare_entity_rep__healthcare_entity__name,
        },
        title: healthcare_entity_rep__title,
      };
    }
    try {
      await mutationCompleteRegistration.mutateAsync(userCompleteRegistrationData);
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
    }
  };

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="logo-container">
                <Logo className="logo" variant="color" />
              </div>
              <div className="mb-3 small text-muted bg-primary-muted rounded p-2">
                Please complete your registration by providing the following information.
              </div>
              <FormFeedback
                serverErrors={(mutationCompleteRegistration.error as any)?.body?.non_field_errors}
              />
              <FormTextField
                name="first_name"
                label="First Name"
                register={register}
                errors={errors}
                required
              />
              <FormTextField
                name="last_name"
                label="Last Name"
                register={register}
                errors={errors}
                required
              />
              <FormInputMaskField
                name="phone_number"
                label="Phone Number"
                mask="(___) ___-____"
                replacement={{ _: /\d/ }}
                placeholder="(123) 456-7890"
                errors={errors}
                register={register}
                required
              />
              {user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP && (
                <>
                  <FormTextField
                    name="healthcare_entity_rep__healthcare_entity__name"
                    label="Healthcare Entity Name"
                    register={register}
                    errors={errors}
                  />
                  <FormTextField
                    name="healthcare_entity_rep__title"
                    label="Job Title"
                    register={register}
                    errors={errors}
                  />
                </>
              )}
              <div className="d-flex align-items-start mb-3">
                <FormCheckboxField
                  name="text_opt_in"
                  label=""
                  errors={errors}
                  register={register}
                  className="mb-0 flex-shrink-0 d-inline-flex"
                />
                <small className="text-muted">
                  I consent to receive notifications from Sendit about account activity via SMS.
                  Message & data rates may apply. Message frequency depends on account activity.
                  Reply STOP to opt out, HELP for help.{' '}
                  <a
                    href="https://www.senditgigs.com/terms-of-service/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>{' '}
                  &{' '}
                  <a
                    href="https://www.senditgigs.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </small>
              </div>
              <div className="d-grid">
                <Button
                  size="lg"
                  className="btn-shadow w-100"
                  type="submit"
                  variant="primary"
                  onClick={() => handleSubmitClick()}
                  disabled={mutationCompleteRegistration.isLoading || !isSubmitEnabled}
                >
                  Complete Registration
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
